import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AnalyticsContext } from 'contexts/analytics';
import { ICounters } from 'contexts/analytics/types';
import { SessionContext } from 'contexts/session';
import Notification from 'components/notification';
import { BuildPreflightPayloadArgs } from 'utils/analyticsPageExport/preflight';
import { downloadExportBlob, runPageExportGenerate } from 'utils/analyticsPageExport/generate';
import { useExportChartCapture } from '../ExportChartCapture';
import { ExportFormat, usePageExportPreflight } from './usePageExportPreflight';
import {
  COOLDOWN_MS,
  DEFAULT_THROTTLE_SECONDS,
  GENERATE_ERROR_MESSAGES,
  throttleMessage,
} from './constants';

// Page-level "does the account have any data" check, mirroring the analytics
// metric tiles. Tab-independent, so it blocks only genuinely-empty accounts.
const hasAnalyticsData = (counters?: ICounters): boolean =>
  Boolean(
    counters?.users?.withActivity ||
      counters?.activities?.overall ||
      counters?.videos?.watched ||
      counters?.quizzes?.passed ||
      counters?.labs?.started
  );

interface UsePageExportGenerateReturn {
  requestExport: (format: ExportFormat) => void;
  warningModal: JSX.Element;
  hostElement: JSX.Element;
  isBusy: boolean;
  isDisabled: boolean;
}

export function usePageExportGenerate(): UsePageExportGenerateReturn {
  const { filters, currentChart, entitiesTable, tableOrdering, counters } =
    useContext(AnalyticsContext);
  const session = useContext(SessionContext);

  const capture = useExportChartCapture();
  const [isExporting, setIsExporting] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState(0);
  const inFlightRef = useRef(false);
  const lastSignatureRef = useRef<string | null>(null);
  const lastSubmitRef = useRef(0);

  useEffect(() => {
    if (blockedUntil <= Date.now()) return undefined;
    const timer = setTimeout(() => setBlockedUntil(0), blockedUntil - Date.now());
    return () => clearTimeout(timer);
  }, [blockedUntil]);

  const applyThrottle = useCallback((retryAfter?: number) => {
    const seconds = retryAfter ?? DEFAULT_THROTTLE_SECONDS;
    setBlockedUntil((prev) => Math.max(prev, Date.now() + seconds * 1000));
    Notification({ type: 'error', text: throttleMessage(seconds) });
  }, []);

  const buildArgs = useCallback(
    (format: ExportFormat): BuildPreflightPayloadArgs => {
      const managedTeamIds = (session?.user?.teams ?? [])
        .filter((team) => team.is_manager)
        .map((team) => team.team_id);
      const teamIds = filters?.team_ids
        ? filters.team_ids.split(',').filter(Boolean)
        : session?.isAccountOwner
        ? []
        : managedTeamIds;
      return {
        format,
        accountId: session?.account?.id,
        teamIds,
        dateFrom: filters?.date_from,
        dateTo: filters?.date_to,
        period: filters?.period,
        currentChart,
        currentEntity: entitiesTable?.currentEntity,
        tableOrdering,
      };
    },
    [filters, session, currentChart, entitiesTable, tableOrdering]
  );

  const generate = useCallback(
    async (format: ExportFormat) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setIsExporting(true);
      try {
        const charts = await capture.captureAll();
        const result = await runPageExportGenerate(buildArgs(format), charts);
        if (result.ok === false) {
          if (result.reason === 'throttled') {
            applyThrottle(result.retryAfter);
            return;
          }
          Notification({ type: 'error', text: GENERATE_ERROR_MESSAGES[result.reason] });
          return;
        }
        downloadExportBlob(result.blob, result.filename);
      } catch {
        Notification({ type: 'error', text: GENERATE_ERROR_MESSAGES.request_failed });
      } finally {
        inFlightRef.current = false;
        setIsExporting(false);
        setBlockedUntil((prev) => Math.max(prev, Date.now() + COOLDOWN_MS));
      }
    },
    [capture, buildArgs, applyThrottle]
  );

  const preflight = usePageExportPreflight({ onProceed: generate, onThrottled: applyThrottle });

  const isBusy = preflight.isPreflighting || isExporting;
  const isDisabled = blockedUntil > Date.now();

  const requestExport = useCallback(
    (format: ExportFormat) => {
      const signature = `${format}:${JSON.stringify(buildArgs(format))}`;
      const isDuplicate =
        signature === lastSignatureRef.current && Date.now() - lastSubmitRef.current < COOLDOWN_MS;
      if (isBusy || blockedUntil > Date.now() || isDuplicate) return;
      if (!hasAnalyticsData(counters)) {
        Notification({ type: 'error', text: GENERATE_ERROR_MESSAGES.no_data });
        return;
      }
      lastSignatureRef.current = signature;
      lastSubmitRef.current = Date.now();
      preflight.startExport(format);
    },
    [counters, buildArgs, isBusy, blockedUntil, preflight]
  );

  return {
    requestExport,
    warningModal: preflight.warningModal,
    hostElement: capture.hostElement,
    isBusy,
    isDisabled,
  };
}
